<template>
    <div class="dashboard-container">
      <header class="header">
        <div class="header-content">
          <h1 class="header-title">山西文旅后台管理系统</h1>
          <div class="header-actions">
            <div class="dropdown">
              <button @click="toggleUserMenu" class="dropdown-button">
                <Users class="icon" />
                <span>管理员</span>
                <ChevronDown class="icon" />
              </button>
              <div v-if="userMenuOpen" class="dropdown-menu">
                <a href="#" class="dropdown-item">个人设置</a>
                <a href="#" class="dropdown-item">退出登录</a>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main class="main-content">
        <div class="container">
          <div class="dashboard-header">
            <h2 class="dashboard-title">数据分析面板</h2>
            <div class="dashboard-actions">
              <div class="dropdown">
                <button @click="toggleTimeMenu" class="dropdown-button">
                  <Calendar class="icon" />
                  <span>{{ timeRange }}</span>
                  <ChevronDown class="icon" />
                </button>
                <div v-if="timeMenuOpen" class="dropdown-menu">
                  <a href="#" @click.prevent="setTimeRange('今日')" class="dropdown-item">今日</a>
                  <a href="#" @click.prevent="setTimeRange('本周')" class="dropdown-item">本周</a>
                  <a href="#" @click.prevent="setTimeRange('本月')" class="dropdown-item">本月</a>
                  <a href="#" @click.prevent="setTimeRange('季度')" class="dropdown-item">季度</a>
                  <a href="#" @click.prevent="setTimeRange('年度')" class="dropdown-item">年度</a>
                </div>
              </div>
              <button class="filter-button">
                <Filter class="icon" />
                <span>筛选</span>
              </button>
            </div>
          </div>
  
          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-header">
                <div class="stat-label">总访问量</div>
                <div class="stat-value">1,234,567</div>
              </div>
              <div class="stat-footer">
                <span class="stat-increase">↑ 12.5%</span> 较上月
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-header">
                <div class="stat-label">景点预订量</div>
                <div class="stat-value">89,432</div>
              </div>
              <div class="stat-footer">
                <span class="stat-increase">↑ 8.3%</span> 较上月
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-header">
                <div class="stat-label">用户评论数</div>
                <div class="stat-value">45,678</div>
              </div>
              <div class="stat-footer">
                <span class="stat-increase">↑ 15.2%</span> 较上月
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-header">
                <div class="stat-label">平均停留时间</div>
                <div class="stat-value">4.5 分钟</div>
              </div>
              <div class="stat-footer">
                <span class="stat-decrease">↓ 2.1%</span> 较上月
              </div>
            </div>
          </div>
  
          <div class="tabs-container">
            <div class="tabs-header">
              <a 
                href="#" 
                @click.prevent="activeTab = 'attraction'" 
                :class="['tab-item', { active: activeTab === 'attraction' }]"
              >
                景点分析
              </a>
              <a 
                href="#" 
                @click.prevent="activeTab = 'comment'" 
                :class="['tab-item', { active: activeTab === 'comment' }]"
              >
                评论分析
              </a>
            </div>
            
            <div v-if="activeTab === 'attraction'" class="tab-content">
              <div class="charts-grid">
                <div class="chart-card">
                  <div class="chart-header">
                    <div class="chart-title">景点点击率分析</div>
                    <div class="chart-subtitle">各景点点击率百分比</div>
                  </div>
                  <div class="chart-container">
                    <v-chart class="chart" :option="attractionClickData" autoresize />
                  </div>
                </div>
                <div class="chart-card">
                  <div class="chart-header">
                    <div class="chart-title">景点点击率趋势</div>
                    <div class="chart-subtitle">热门景点点击率变化趋势</div>
                  </div>
                  <div class="chart-container">
                    <v-chart class="chart" :option="clickTrendData" autoresize />
                  </div>
                </div>
              </div>
            </div>
            
            <div v-if="activeTab === 'comment'" class="tab-content">
              <div class="charts-grid">
                <div class="chart-card">
                  <div class="chart-header">
                    <div class="chart-title">用户评论高频词分析</div>
                    <div class="chart-subtitle">评论中出现频率最高的关键词</div>
                  </div>
                  <div class="chart-container">
                    <v-chart class="chart" :option="wordCloudData" autoresize />
                  </div>
                </div>
                <div class="chart-card">
                  <div class="chart-header">
                    <div class="chart-title">用户评论情感分析</div>
                    <div class="chart-subtitle">评论情感倾向分布</div>
                  </div>
                  <div class="chart-container">
                    <v-chart class="chart" :option="sentimentData" autoresize />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted } from 'vue';
  import { use } from 'echarts/core';
  import { CanvasRenderer } from 'echarts/renderers';
  import { BarChart, LineChart, PieChart } from 'echarts/charts';
  import {
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    GridComponent
  } from 'echarts/components';
  import VChart from 'vue-echarts';
  import 'echarts-wordcloud';
  import { Calendar, ChevronDown, Filter, Users } from 'lucide-vue-next';
  
  // 注册 ECharts 组件
  use([
    CanvasRenderer,
    BarChart,
    LineChart,
    PieChart,
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    GridComponent
  ]);
  
  // 响应式状态
  const timeRange = ref('本月');
  const activeTab = ref('attraction');
  const userMenuOpen = ref(false);
  const timeMenuOpen = ref(false);
  
  // 方法
  const toggleUserMenu = () => {
    userMenuOpen.value = !userMenuOpen.value;
    if (userMenuOpen.value) timeMenuOpen.value = false;
  };
  
  const toggleTimeMenu = () => {
    timeMenuOpen.value = !timeMenuOpen.value;
    if (timeMenuOpen.value) userMenuOpen.value = false;
  };
  
  const setTimeRange = (range) => {
    timeRange.value = range;
    timeMenuOpen.value = false;
  };
  
  // 点击页面其他地方关闭菜单
  onMounted(() => {
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.dropdown')) {
        userMenuOpen.value = false;
        timeMenuOpen.value = false;
      }
    });
  });
  
  // 景点点击率数据
  const attractionClickData = {
    title: {
      text: '景点点击率分析',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: ['平遥古城', '五台山', '云冈石窟', '乔家大院', '壶口瀑布', '太原古县城', '王家大院']
    },
    yAxis: {
      type: 'value',
      name: '点击率 (%)'
    },
    series: [
      {
        name: '点击率',
        type: 'bar',
        data: [28.5, 23.7, 22.1, 18.9, 17.2, 15.8, 14.3],
        itemStyle: {
          color: '#10b981'
        }
      }
    ]
  };
  
  // 点击率趋势数据
  const clickTrendData = {
    title: {
      text: '景点点击率趋势',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis'
    },
    legend: {
      data: ['平遥古城', '五台山', '云冈石窟'],
      bottom: 0
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: ['1月', '2月', '3月', '4月', '5月', '6月']
    },
    yAxis: {
      type: 'value',
      name: '点击率 (%)'
    },
    series: [
      {
        name: '平遥古城',
        type: 'line',
        data: [18.2, 19.5, 22.1, 25.3, 27.8, 28.5],
        smooth: true
      },
      {
        name: '五台山',
        type: 'line',
        data: [15.3, 16.8, 18.2, 20.5, 22.1, 23.7],
        smooth: true
      },
      {
        name: '云冈石窟',
        type: 'line',
        data: [14.2, 15.5, 17.8, 19.2, 21.5, 22.1],
        smooth: true
      }
    ]
  };
  
  // 用户评论高频词分析
  const wordCloudData = {
    title: {
      text: '用户评论高频词分析',
      left: 'center'
    },
    tooltip: {},
    series: [{
      type: 'wordCloud',
      shape: 'circle',
      left: 'center',
      top: 'center',
      width: '90%',
      height: '90%',
      right: null,
      bottom: null,
      sizeRange: [12, 60],
      rotationRange: [-90, 90],
      rotationStep: 45,
      gridSize: 8,
      drawOutOfBound: false,
      textStyle: {
        fontFamily: 'sans-serif',
        fontWeight: 'bold',
        color: function () {
          return 'rgb(' + [
            Math.round(Math.random() * 160),
            Math.round(Math.random() * 160),
            Math.round(Math.random() * 160)
          ].join(',') + ')'
        }
      },
      emphasis: {
        textStyle: {
          shadowBlur: 10,
          shadowColor: '#333'
        }
      },
      data: [
        { name: '美丽', value: 10000 },
        { name: '历史', value: 8500 },
        { name: '文化', value: 8200 },
        { name: '壮观', value: 7800 },
        { name: '古老', value: 7200 },
        { name: '传统', value: 6800 },
        { name: '建筑', value: 6500 },
        { name: '景色', value: 6300 },
        { name: '推荐', value: 6000 },
        { name: '值得', value: 5800 },
        { name: '特色', value: 5500 },
        { name: '美食', value: 5300 },
        { name: '体验', value: 5000 },
        { name: '服务', value: 4800 },
        { name: '交通', value: 4500 },
        { name: '方便', value: 4300 },
        { name: '人少', value: 4000 },
        { name: '人多', value: 3800 },
        { name: '拥挤', value: 3500 },
        { name: '票价', value: 3300 },
        { name: '导游', value: 3000 },
        { name: '讲解', value: 2800 },
        { name: '住宿', value: 2500 },
        { name: '山西', value: 2300 },
        { name: '晋商', value: 2000 },
        { name: '佛教', value: 1800 },
        { name: '古镇', value: 1500 },
        { name: '瀑布', value: 1300 },
        { name: '石窟', value: 1000 }
      ]
    }]
  };
  
  // 评论情感分析
  const sentimentData = {
    title: {
      text: '用户评论情感分析',
      left: 'center'
    },
    tooltip: {
      trigger: 'item'
    },
    legend: {
      orient: 'horizontal',
      bottom: 'bottom'
    },
    series: [
      {
        name: '情感分布',
        type: 'pie',
        radius: '50%',
        data: [
          { value: 68, name: '正面' },
          { value: 22, name: '中性' },
          { value: 10, name: '负面' }
        ],
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        itemStyle: {
          color: function(params) {
            const colorList = ['#10b981', '#f59e0b', '#ef4444'];
            return colorList[params.dataIndex];
          }
        }
      }
    ]
  };
  </script>
  
  <style>
  :root {
    --primary-color: #10b981;
    --primary-hover: #059669;
    --text-color: #333333;
    --text-light: #6b7280;
    --border-color: #e5e7eb;
    --card-bg: #ffffff;
    --bg-color: #f9fafb;
    --success-color: #10b981;
    --warning-color: #f59e0b;
    --danger-color: #ef4444;
    --shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  /* 全局样式 */
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    color: var(--text-color);
    background-color: var(--bg-color);
    line-height: 1.5;
  }
  
  .icon {
    width: 16px;
    height: 16px;
  }
  
  /* 布局 */
  .dashboard-container {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }
  
  .container {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 16px;
  }
  
  .header {
    border-bottom: 1px solid var(--border-color);
    background-color: var(--card-bg);
  }
  
  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 64px;
    padding: 0 16px;
  }
  
  .header-title {
    font-size: 18px;
    font-weight: 600;
  }
  
  .header-actions {
    display: flex;
    align-items: center;
  }
  
  .main-content {
    flex: 1;
    padding: 24px 0;
  }
  
  /* 仪表盘头部 */
  .dashboard-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }
  
  .dashboard-title {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.025em;
  }
  
  .dashboard-actions {
    display: flex;
    gap: 8px;
  }
  
  /* 统计卡片 */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 16px;
    margin-bottom: 24px;
  }
  
  @media (min-width: 768px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  
  @media (min-width: 1024px) {
    .stats-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }
  
  .stat-card {
    background-color: var(--card-bg);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: 16px;
    box-shadow: var(--shadow);
  }
  
  .stat-header {
    margin-bottom: 8px;
  }
  
  .stat-label {
    font-size: 14px;
    color: var(--text-light);
  }
  
  .stat-value {
    font-size: 24px;
    font-weight: 600;
  }
  
  .stat-footer {
    font-size: 12px;
    color: var(--text-light);
  }
  
  .stat-increase {
    color: var(--success-color);
    font-weight: 500;
  }
  
  .stat-decrease {
    color: var(--danger-color);
    font-weight: 500;
  }
  
  /* 标签页 */
  .tabs-container {
    margin-top: 24px;
  }
  
  .tabs-header {
    display: flex;
    border-bottom: 1px solid var(--border-color);
    margin-bottom: 16px;
  }
  
  .tab-item {
    padding: 12px 16px;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-light);
    text-decoration: none;
    border-bottom: 2px solid transparent;
    margin-right: 16px;
  }
  
  .tab-item.active {
    color: var(--primary-color);
    border-bottom-color: var(--primary-color);
  }
  
  .tab-content {
    margin-top: 24px;
  }
  
  /* 图表网格 */
  .charts-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
  }
  
  @media (min-width: 768px) {
    .charts-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  
  .chart-card {
    background-color: var(--card-bg);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: 16px;
    box-shadow: var(--shadow);
  }
  
  .chart-header {
    margin-bottom: 16px;
  }
  
  .chart-title {
    font-size: 18px;
    font-weight: 600;
  }
  
  .chart-subtitle {
    font-size: 14px;
    color: var(--text-light);
  }
  
  .chart-container {
    height: 400px;
  }
  
  .chart {
    width: 100%;
    height: 100%;
  }
  
  /* 下拉菜单 */
  .dropdown {
    position: relative;
  }
  
  .dropdown-button {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background-color: var(--card-bg);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
  }
  
  .dropdown-menu {
    position: absolute;
    right: 0;
    top: 100%;
    margin-top: 4px;
    width: 160px;
    background-color: var(--card-bg);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    z-index: 10;
  }
  
  .dropdown-item {
    display: block;
    padding: 8px 16px;
    font-size: 14px;
    color: var(--text-color);
    text-decoration: none;
  }
  
  .dropdown-item:hover {
    background-color: var(--bg-color);
  }
  
  .filter-button {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background-color: var(--card-bg);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
  }
  </style>